import { useEffect, useState } from "react";
import {
    subscribeToStorageKey,
} from "../utils/localStorageEmitter";
import {
    LOCALSTORAGE_MEDIUM_CURRENT,
    type CachedMedium,
} from "./useCachedMedium";
import {
    LOCALSTORAGE_TERREIRO_CURRENT,
    type CachedTerreiro,
} from "./useCachedTerreiro";

interface PermissionContext {
  terreiroId: string;
  mediumId: string | undefined;
  isPersonalProfile: boolean;
}

/**
 * Reads BOTH terreiro and medium from localStorage atomically in a single read.
 * This avoids the race condition where two independent polling hooks
 * (useCachedTerreiro + useCachedMedium) detect changes at different times,
 * causing a request with new mediumId + stale terreiroId.
 */
function readContextFromStorage(): PermissionContext {
  try {
    if (typeof window === "undefined" || !window.localStorage) {
      return { terreiroId: "", mediumId: undefined, isPersonalProfile: false };
    }

    // Read both values in the same synchronous block
    const rawTerreiro = localStorage.getItem(LOCALSTORAGE_TERREIRO_CURRENT);
    const rawMedium = localStorage.getItem(LOCALSTORAGE_MEDIUM_CURRENT);

    let terreiro: CachedTerreiro | null | undefined;
    try {
      if (rawTerreiro) {
        const parsed = JSON.parse(rawTerreiro);
        terreiro = parsed === null ? null : (parsed as CachedTerreiro);
      }
    } catch {
      terreiro = undefined;
    }

    let medium: CachedMedium | null | undefined;
    try {
      if (rawMedium) {
        const parsed = JSON.parse(rawMedium);
        medium = parsed === null ? null : (parsed as CachedMedium);
      }
    } catch {
      medium = undefined;
    }

    const isPersonalProfile = terreiro === null;
    const terreiroId = !isPersonalProfile ? (terreiro?.id ?? "") : "";
    const mediumId = medium?.id;

    // Debug: Log context reading
    if (typeof window !== "undefined" && window.__DEV_CACHED_CONTEXT__) {
      console.log("[useCachedPermissionContext] Context read:", {
        terreiro,
        medium,
        isPersonalProfile,
        terreiroId,
        mediumId,
        rawTerreiro,
        rawMedium,
      });
    }

    return { terreiroId, mediumId, isPersonalProfile };
  } catch {
    return { terreiroId: "", mediumId: undefined, isPersonalProfile: false };
  }
}

/**
 * Hook that provides a coherent terreiro+medium context for permission queries.
 * Both values are always read atomically from localStorage in the same tick,
 * preventing race conditions when switching profiles.
 */
export function useCachedPermissionContext(): PermissionContext {
  const [context, setContext] = useState<PermissionContext>(
    readContextFromStorage,
  );

  useEffect(() => {
    const update = () => setContext(readContextFromStorage());

    // Subscribe to both keys — either change triggers an atomic read of both
    const unsub1 = subscribeToStorageKey(LOCALSTORAGE_TERREIRO_CURRENT, update);
    const unsub2 = subscribeToStorageKey(LOCALSTORAGE_MEDIUM_CURRENT, update);

    return () => {
      unsub1();
      unsub2();
    };
  }, []);

  return context;
}
